<template>
  <div data-test-id="login-challenge">
    <div class="text-center">
      <h3
        class="otp-title mt-2"
        data-test-id="login-challenge-title">OTP VERIFICATION</h3>
      <b-img
        :src="img_change_password"
        data-test-id="login-challenge-image"
        width="200" />
    </div>
    <div class="text-justify mt-4">
      <p
        class="otp-description"
        data-test-id="login-challenge-description">{{ description }}</p>
    </div>
    <div
      id="otp-code"
      aria-label="Verification code"
      data-test-id="login-challenge-code"
      role="group">
      <input
        v-for="(digit, i) in digits"
        ref="box"
        :key="i"
        :aria-label="`Digit ${i + 1} of 6`"
        :autocomplete="i === 0 ? 'one-time-code' : 'off'"
        :class="{ invalid }"
        :data-test-id="`login-challenge-digit-${i + 1}`"
        :disabled="locked"
        :value="digit"
        class="otp"
        inputmode="numeric"
        type="text"
        @focus="$event.target.select()"
        @input="onInput(i, $event)"
        @keydown.delete="onDelete(i, $event)"
        @paste.prevent="onPaste">
    </div>
    <div
      aria-live="polite"
      class="command-box text-center">
      <!-- Order follows the design: the error sits under the boxes, the timers below it. -->
      <div
        v-if="locked"
        class="locked-box mt-3 mb-2"
        data-test-id="login-challenge-locked">
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          aria-hidden="true">
          <circle
            cx="12"
            cy="12"
            r="9"
            fill="none"
            stroke="#C62828"
            stroke-width="2" />
          <path
            d="M12 7 V12 L15 14"
            fill="none"
            stroke="#C62828"
            stroke-width="2"
            stroke-linecap="round" />
        </svg>
        <p>
          <strong>Too many incorrect codes.</strong>
          For your security, please wait {{ waitText(lockLeft) }} before trying again.
        </p>
      </div>
      <p
        v-else-if="expired"
        class="mt-3 mb-1 red"
        data-test-id="login-challenge-expired">
        This code has expired. Please request a new code.
      </p>
      <p
        v-if="message"
        class="mt-3 mb-1 red"
        data-test-id="login-challenge-message">{{ message }}</p>
      <p
        v-if="!locked && !expired"
        class="otp-status mt-3 mb-1"
        data-test-id="login-challenge-status">
        Code is valid for <strong>{{ clock(codeLeft) }}</strong> minutes
      </p>
      <p class="otp-resend">
        <span>Didn't receive the code? </span>
        <button
          :disabled="!canResend"
          class="resend-btn"
          data-test-id="login-challenge-resend"
          type="button"
          @click="resendNow">{{ resendLabel }}</button>
      </p>
    </div>
    <div class="otp-actions mb-2">
      <b-button
        :disabled="busy"
        class="otp-btn otp-btn--close"
        data-test-id="login-challenge-close"
        @click="closeStep">
        Close
      </b-button>
      <b-button
        :disabled="!canVerify"
        class="otp-btn otp-btn--verify"
        data-test-id="login-challenge-verify"
        @click="sendNow">
        {{ busy ? 'Verifying...' : 'Verify' }}
      </b-button>
    </div>
  </div>
</template>
<script>
import { mapActions, mapMutations, mapState } from 'vuex';
import waitText from '@/utils/waitText';

const LENGTH = 6;
const CODE_FORMAT_MESSAGE = 'Please enter the 6-digit numeric code.';
const blank = () => Array(LENGTH).fill('');
const pad = n => (n < 10 ? `0${n}` : `${n}`);

// The two-factor sign-in code step. jLogin renders it only while a challenge is pending,
// so the legacy LOGIN_OTP step (jInputOtp) is untouched.
export default {
  props: {
    otpShow: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      digits: blank(),
      codeLeft: 0,
      resendLeft: 0,
      // null when not locked, else the seconds left of the lock.
      lockLeft: null,
      invalid: false,
      message: '',
      busy: false,
      timer: null,
    };
  },
  computed: {
    ...mapState('jStoreLogin', ['login_challenge']),

    code() {
      return this.digits.join('');
    },
    locked() {
      return this.lockLeft !== null;
    },
    expired() {
      return this.codeLeft <= 0;
    },
    canVerify() {
      return this.code.length === LENGTH && !this.expired && !this.locked && !this.busy;
    },
    canResend() {
      return this.resendLeft <= 0 && !this.locked && !this.busy;
    },
    resendLabel() {
      if (this.locked) return 'Resend code is unavailable';
      return this.resendLeft > 0 ? `Resend code in ${this.clock(this.resendLeft)}` : 'Resend code';
    },
    description() {
      return 'For enhanced security, you are required to enter a one-time password (OTP) to sign in. '
        + `An email with the OTP has been sent to your email address ${this.login_challenge.masked_email}.`;
    },
    img_change_password() {
      // eslint-disable-next-line global-require
      return require('@/assets/img/otp-email.png');
    },
  },
  watch: {
    otpShow(val) {
      if (val) this.restart();
      else this.stopCount();
    },
  },
  // The legacy step may already be open when its code is answered with a challenge.
  mounted() {
    if (this.otpShow) this.restart();
  },
  beforeDestroy() {
    this.stopCount();
  },
  methods: {
    ...mapActions('jStoreLogin', ['verifyLoginChallenge', 'resendLoginChallenge']),
    ...mapMutations('jStoreLogin', ['clearLoginChallenge']),

    waitText,
    clock(seconds) {
      const s = Math.max(0, seconds || 0);
      return `${pad(Math.floor(s / 60))}:${pad(s % 60)}`;
    },
    focusBox(i) {
      this.$nextTick(() => {
        // eslint-disable-next-line security/detect-object-injection
        const box = this.$refs.box && this.$refs.box[i];
        if (box) box.focus();
      });
    },
    // Writes digits from box `start`; anything that is not a digit is dropped.
    fill(start, text) {
      const clean = String(text).replace(/\D/g, '').slice(0, LENGTH - start);
      this.invalid = false;
      this.message = '';
      if (!clean) {
        this.$set(this.digits, start, '');
        return;
      }
      clean.split('').forEach((c, k) => this.$set(this.digits, start + k, c));
      this.focusBox(Math.min(start + clean.length, LENGTH - 1));
    },
    onInput(i, e) {
      this.fill(i, e.target.value);
      // eslint-disable-next-line security/detect-object-injection
      e.target.value = this.digits[i];
    },
    onPaste(e) {
      const data = e.clipboardData || window.clipboardData;
      const pasted = data ? data.getData('text') : '';
      if (!/\d/.test(pasted)) return;
      this.digits = blank();
      this.fill(0, pasted);
    },
    onDelete(i, e) {
      if (e.target.value === '' && i > 0) this.focusBox(i - 1);
    },
    // Clears the boxes and errors, then restarts both countdowns.
    restart() {
      this.stopCount();
      this.digits = blank();
      this.invalid = false;
      this.message = '';
      this.lockLeft = null;
      this.startCount();
      this.focusBox(0);
    },
    // ponytail: counters tick once a second, so a throttled background tab drifts.
    // Switch to a local deadline if that matters.
    startCount() {
      this.codeLeft = this.login_challenge.code_expires_in_seconds;
      this.resendLeft = this.login_challenge.resend_available_in_seconds;
      this.timer = setInterval(() => {
        if (this.codeLeft > 0) this.codeLeft -= 1;
        if (this.resendLeft > 0) this.resendLeft -= 1;
        if (this.locked) {
          this.lockLeft -= 1;
          if (this.lockLeft <= 0) this.endSession();
        }
      }, 1000);
    },
    stopCount() {
      clearInterval(this.timer);
      this.timer = null;
    },
    // The sign-in session is gone: drop the challenge and let jLogin show an empty form.
    endSession() {
      this.stopCount();
      this.clearLoginChallenge();
      this.$emit('session-ended');
    },
    closeStep() {
      this.stopCount();
      this.clearLoginChallenge();
      this.$emit('close-modal');
    },
    async sendNow() {
      if (!this.canVerify) return;
      this.busy = true;
      this.invalid = false;
      this.message = '';
      try {
        // Success reloads the page, so the code and the busy state stay until it leaves.
        await this.verifyLoginChallenge(this.code);
      } catch (err) {
        this.busy = false;
        this.showError(err);
      }
    },
    async resendNow() {
      if (!this.canResend) return;
      this.busy = true;
      this.message = '';
      try {
        await this.resendLoginChallenge();
        this.restart();
      } catch (err) {
        this.showError(err);
      } finally {
        this.busy = false;
      }
    },
    // The server wait wins over a local countdown that drifted to zero.
    syncResendCooldown(seconds) {
      if (seconds) this.resendLeft = seconds;
    },
    // Maps an error code from the error catalog to the controls of the code step.
    showError(err) {
      const { status, data } = err.response || {};
      const { code, message } = data || {};
      if (code === 'two_factor_locked') {
        // The design shows the locked boxes empty and grey.
        this.digits = blank();
        this.invalid = false;
        this.lockLeft = err.cooldownSeconds || 0;
        if (this.lockLeft <= 0) this.endSession();
      } else if (code === 'login_challenge_not_found') {
        this.endSession();
      } else if (code === 'two_factor_resend_too_early') {
        this.syncResendCooldown(err.cooldownSeconds);
      } else if (code === 'two_factor_code_invalid') {
        this.invalid = true;
        this.message = message;
      } else if (status === 400 && !code) {
        // Request validation rejects a malformed code: no code, validator text.
        this.message = CODE_FORMAT_MESSAGE;
      } else if (code === 'two_factor_code_expired') {
        this.codeLeft = 0;
      } else {
        this.$store.dispatch('jStoreNotificationScreen/toggleProblem', message || err.message);
      }
    },
  },
};
</script>

<style scoped>
#otp-code {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  margin-top: 4px;
}

.otp-title{
  font-family: 'Poppins-SemiBold';
  font-size: 17px;
  color: #333333;
}

.otp-description{
  font-size: 12px;
  line-height: 1.6;
  color: #444a52;
}

.otp-status{
  font-size: 12px;
  color: #444a52;
}

.otp-resend{
  font-size: 12px;
  color: #6b717a;
}

.otp-actions{
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 8px;
}

.otp-btn{
  height: 32px;
  padding: 0 18px;
  border: none;
  border-radius: 6px;
  color: #ffffff;
  font-size: 12px;
  font-weight: 500;
}

.otp-btn--close{
  background: #757575;
}

.otp-btn--close:disabled{
  opacity: 0.6;
}

.otp-btn--verify{
  background: #3d7fe0;
}

.otp-btn--verify:disabled{
  background: #a9c7f4;
  opacity: 1;
}

input.otp{
  width: 44px;
  height: 48px;
  box-sizing: border-box;
  border: 1.5px solid #c9cdd3;
  border-radius: 6px;
  text-align: center;
  font-size: 20px;
  font-weight: 600;
  color: #333333;
  outline: none;
}

input.otp:focus{
  border-color: #3d7fe0;
}

input.otp.invalid{
  border-color: #c62828;
}

input.otp:disabled{
  background: #f3f4f6;
  border-color: #e1e4e8;
}

.locked-box{
  display: flex;
  gap: 10px;
  align-items: flex-start;
  text-align: left;
  background: #fdecec;
  border: 1px solid #f3c1c1;
  border-radius: 8px;
  padding: 10px 12px;
}

.locked-box svg{
  flex: none;
  margin-top: 1px;
}

.locked-box p{
  margin: 0;
  font-size: 12px;
  line-height: 1.5;
  color: #8e1b1b;
}

/* Weights come from the font family (base/_typography.scss), not font-weight. */
.locked-box strong,
.otp-status strong{
  font-family: 'Poppins-Bold';
  font-weight: normal;
}

.resend-btn{
  padding: 0;
  border: 0;
  background: none;
  color: #3d7fe0;
  font-size: 12px;
  font-weight: 600;
}

.resend-btn:disabled{
  background: none;
  color: #9aa0a8;
  cursor: not-allowed;
}

.command-box{
  font-weight: bold;
}

.red {
  color: #c62828;
  font-size: 12px;
}

p {
  font-weight: 400 !important;
}

p.red {
  font-weight: 500 !important;
}
</style>



// WEBPACK FOOTER //
// src/components/inputs/jInputLoginChallenge.vue